import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { Savings } from '@mui/icons-material';
import { MONTHS, MONTH_LABELS } from './CampaignFilters';

const CURRENT_YEAR = new Date().getFullYear();
// Meesha's earliest Financial Year (see CampaignFilters' FINANCIAL_YEARS) starts
// in 2022 — matches the earliest month/year a revenue could plausibly be locked.
const YEARS = Array.from({ length: CURRENT_YEAR - 2022 + 1 }, (_, i) => CURRENT_YEAR - i);

// Super-admin-only revenue month+year filter. Deliberately separate from
// CampaignFilters' own "Month" select, which matches a campaign's *creation*
// month (derived from its _id) rather than when its revenue was locked.
const RevenueMonthFilter = React.memo(function RevenueMonthFilter({ value, onApply, onClear }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingMonth, setPendingMonth] = useState(value?.month || MONTHS[1].value);
    const [pendingYear, setPendingYear] = useState(value?.year || CURRENT_YEAR);
    const open = Boolean(anchorEl);

    const openPopover = (e) => {
        setPendingMonth(value?.month || MONTHS[1].value);
        setPendingYear(value?.year || CURRENT_YEAR);
        setAnchorEl(e.currentTarget);
    };

    const handleApply = () => {
        onApply(pendingMonth, pendingYear);
        setAnchorEl(null);
    };

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Button
                variant="outlined"
                size="small"
                startIcon={<Savings fontSize="small" />}
                onClick={openPopover}
                sx={{ color: '#7C3AED', borderColor: '#7C3AED' }}
            >
                Revenue
            </Button>

            {value && (
                <Chip
                    label={`Revenue: ${MONTH_LABELS[value.month] || value.month} ${value.year}`}
                    size="small"
                    onDelete={onClear}
                />
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ p: 2.5, width: 280 }}>
                    <Stack spacing={2}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Net Revenue By Month
                        </Typography>

                        <Stack direction="row" spacing={1.5}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="revenue-month-label">Month</InputLabel>
                                <Select
                                    labelId="revenue-month-label"
                                    label="Month"
                                    value={pendingMonth}
                                    onChange={(e) => setPendingMonth(e.target.value)}
                                >
                                    {MONTHS.filter((m) => m.value !== 'All').map(({ value: v, label }) => (
                                        <MenuItem key={v} value={v}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="revenue-year-label">Year</InputLabel>
                                <Select
                                    labelId="revenue-year-label"
                                    label="Year"
                                    value={pendingYear}
                                    onChange={(e) => setPendingYear(e.target.value)}
                                >
                                    {YEARS.map((y) => (
                                        <MenuItem key={y} value={y}>
                                            {y}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Divider />

                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <Button size="small" onClick={() => setAnchorEl(null)}>
                                Cancel
                            </Button>
                            <Button size="small" variant="contained" onClick={handleApply}>
                                Apply
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Popover>
        </Stack>
    );
});

export default RevenueMonthFilter;
